import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { HomeExpenseGroupCategoryDto } from '../../types/homeExpenses';

interface CategoryListProps {
  categories: HomeExpenseGroupCategoryDto[];
  onEdit: (category: HomeExpenseGroupCategoryDto) => void;
  onDelete: (categoryId: string) => void;
  loading?: boolean;
}

const CategoryList: React.FC<CategoryListProps> = ({
  categories,
  onEdit,
  onDelete,
  loading = false
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>A carregar categorias...</Typography>
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Nenhuma categoria encontrada. Crie a sua primeira categoria para organizar os grupos de despesas.
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="50px">Ordem</TableCell>
            <TableCell>Nome</TableCell>
            <TableCell>Descrição</TableCell>
            <TableCell width="100px">Cor</TableCell>
            <TableCell width="120px" align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categories
            .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
            .map((category) => (
              <TableRow key={category.publicId} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DragIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                    <Typography variant="body2" color="text.secondary">
                      {category.sortOrder}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="medium">
                    {category.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {category.description || 'Sem descrição'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={category.color}
                    size="small"
                    sx={{
                      backgroundColor: category.color,
                      color: getContrastColor(category.color || '#000'),
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Editar categoria">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(category)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar categoria">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(category.publicId)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Helper function to determine text color based on background
const getContrastColor = (hexColor: string): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

export default CategoryList;
